import { useEffect, useState } from 'react';
import { businessRateAward } from '@/mocks/awards';
import AwardSeal from '@/components/feature/AwardSeal';

const awardFacts = [
  {
    icon: 'ri-trophy-line',
    label: 'Award',
    value: 'Top 10 Award Winner 2026',
  },
  {
    icon: 'ri-map-pin-2-line',
    label: 'Category & Region',
    value: 'Top Publisher in Los Angeles County',
  },
  {
    icon: 'ri-google-fill',
    label: 'Selected on',
    value: 'Verified Google review data',
  },
  {
    icon: 'ri-calendar-check-line',
    label: 'Confirmed',
    value: 'October 9, 2026',
  },
];

export default function AwardRecognition() {
  const [isLetterOpen, setIsLetterOpen] = useState(false);

  useEffect(() => {
    if (!isLetterOpen) return undefined;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsLetterOpen(false);
    };

    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isLetterOpen]);

  return (
    <section id="businessrate-award" className="w-full py-14 md:py-20 bg-cream-100">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Award seal */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative w-full max-w-[340px]">
                <div className="absolute -inset-4 rounded-[2.5rem] bg-gold-400/10 blur-2xl"></div>
                <AwardSeal className="relative" />
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-7">
              <div className="inline-flex flex-wrap items-center gap-2 mb-5">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-gold-50 border border-gold-300 rounded-full">
                  <i className="ri-award-line text-gold-600 text-sm"></i>
                  <span className="text-xs font-medium text-gold-700">Awarded {businessRateAward.dateAwarded}</span>
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                  <span className="text-xs font-medium text-navy-600">Award code {businessRateAward.awardCode}</span>
                </span>
              </div>

              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-4 leading-tight">
                Named a <span className="text-gold-600">Top Publisher in Los Angeles County</span> for 2026
              </h2>

              <p className="text-sm md:text-base text-navy-700 leading-relaxed mb-5">
                BusinessRate recognized Leavitt Peak Press as a <strong>Top 10 Award Winner for 2026</strong> in the
                Top Publisher category for Los Angeles County. The distinction was drawn from verified customer
                feedback and publicly available Google review data, and we can show you the letter itself.
              </p>

              <div className="rounded-2xl bg-white border border-cream-200 p-5 md:p-6 mb-6">
                <p className="text-xs font-semibold text-navy-900 uppercase tracking-[0.16em] mb-3">What the letter says</p>
                <p className="text-sm text-navy-600 leading-relaxed mb-3">
                  {businessRateAward.issuer} states that the recognition is based on{' '}
                  {businessRateAward.basis.toLowerCase()}, drawn from a dataset it describes as &ldquo;
                  {businessRateAward.dataset}&rdquo;. It names Leavitt Peak Press Top Publisher in Los Angeles County
                  for 2026.
                </p>
                <p className="text-sm text-navy-600 leading-relaxed">
                  That is the whole of what we were told about the selection. BusinessRate has not given us its ranking
                  formula, so we are not going to guess at it on their behalf — the award code above is the part you can
                  actually check, and it leads to the same document we are quoting.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {awardFacts.map((fact) => (
                  <div key={fact.label} className="flex items-start gap-3 bg-cream-50 border border-cream-200 rounded-xl px-4 py-3">
                    <div className="w-8 h-8 rounded-lg bg-gold-100 flex items-center justify-center shrink-0">
                      <i className={`${fact.icon} text-gold-600 text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy-400 mb-0.5">{fact.label}</p>
                      <p className="text-xs font-medium text-navy-800 leading-snug">{fact.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setIsLetterOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-text-line"></i>
                  View the Award Letter
                </button>
                <a
                  href={businessRateAward.issuerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-medium text-navy-500 border border-navy-300 rounded-full hover:bg-navy-50 hover:border-navy-500 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  Verify at BusinessRate
                </a>
              </div>
            </div>
          </div>

          {/* Letter preview */}
          <div className="mt-12 md:mt-16 rounded-2xl border border-cream-200 bg-white p-5 md:p-7 flex flex-col md:flex-row items-center gap-6">
            <button
              type="button"
              onClick={() => setIsLetterOpen(true)}
              className="w-[180px] h-[232px] md:w-[200px] md:h-[258px] shrink-0 rounded-xl overflow-hidden border border-cream-200 bg-cream-50 group cursor-pointer"
              aria-label="Open the full BusinessRate 2026 award letter"
            >
              <img
                src={businessRateAward.letterImage}
                alt={businessRateAward.letterAlt}
                title="BusinessRate Top 10 2026 award letter, Top Publisher in Los Angeles County"
                className="w-full h-full object-contain object-top transition-transform duration-500 group-hover:scale-105"
              />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gold-600 uppercase tracking-[0.18em] mb-2">Document on file</p>
              <h3 className="text-lg md:text-xl font-bold text-navy-900 mb-2">
                The original letter, not a badge we made ourselves
              </h3>
              <p className="text-sm text-navy-600 leading-relaxed mb-4">
                Dated {businessRateAward.dateAwarded} and signed by {businessRateAward.signatory}, the letter carries
                the award code <strong>{businessRateAward.awardCode}</strong>, which you can enter at
                businessrate.com/awards to confirm it independently. Click the letter to read it at full size.
              </p>
              <p className="text-xs text-navy-400 leading-relaxed">
                What this award is not: it is not a measure of sales, royalties, or book performance. It reflects
                publicly visible customer review data within a category and region.
              </p>
            </div>
          </div>
        </div>
      </div>

      {isLetterOpen && (
        <div
          className="fixed inset-0 z-[100] bg-navy-950/92 flex items-center justify-center p-4 md:p-8"
          role="dialog"
          aria-modal="true"
          aria-label="BusinessRate 2026 award letter"
          onClick={() => setIsLetterOpen(false)}
        >
          <button
            type="button"
            onClick={() => setIsLetterOpen(false)}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300 cursor-pointer"
            aria-label="Close award letter"
          >
            <i className="ri-close-line text-white text-xl"></i>
          </button>
          <div
            className="w-full max-w-[760px] max-h-[90vh] flex items-center justify-center"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={businessRateAward.letterImage}
              alt={businessRateAward.letterAlt}
              className="w-full h-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </section>
  );
}